/**
 * Utility functions for file handling
 */

/**
 * Ensure a filename is safe for use in URLs and file systems
 * @param {string} filename - The original filename
 * @returns {string} - A safe filename
 */
export const ensureSafeFilename = (filename) => {
  if (!filename) return '';

  // Remove any path information
  let safeName = filename.split(/[\/\\]/).pop();

  // Replace spaces with underscores
  safeName = safeName.replace(/\s+/g, '_');

  // Remove special characters
  safeName = safeName.replace(/[^a-zA-Z0-9_.-]/g, '');

  // Ensure the filename is not too long
  if (safeName.length > 100) {
    const extension = safeName.split('.').pop();
    safeName = safeName.substring(0, 100 - extension.length - 1) + '.' + extension;
  }

  return safeName;
};

/**
 * Get the file extension from a filename
 * @param {string} filename - The filename
 * @returns {string} - The file extension
 */
export const getFileExtension = (filename) => {
  if (!filename) return '';
  return filename.split('.').pop().toLowerCase();
};

/**
 * Check if a file is an image
 * @param {string} filename - The filename
 * @returns {boolean} - Whether the file is an image
 */
export const isImageFile = (filename) => {
  const extension = getFileExtension(filename);
  return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'].includes(extension);
};

/**
 * Check if a file is an audio file
 * @param {string} filename - The filename
 * @returns {boolean} - Whether the file is an audio file
 */
export const isAudioFile = (filename) => {
  const extension = getFileExtension(filename);
  return ['mp3', 'wav', 'ogg', 'flac', 'm4a', 'aac'].includes(extension);
};

/**
 * Check if a file is a document
 * @param {string} filename - The filename
 * @returns {boolean} - Whether the file is a document
 */
export const isDocumentFile = (filename) => {
  const extension = getFileExtension(filename);
  return ['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt'].includes(extension);
};

/**
 * Generate a unique filename
 * @param {string} originalFilename - The original filename
 * @returns {string} - A unique filename
 */
export const generateUniqueFilename = (originalFilename) => {
  const timestamp = Date.now();
  const randomString = Math.random().toString(36).substring(2, 8);
  const extension = getFileExtension(originalFilename);
  const baseName = ensureSafeFilename(originalFilename.replace(`.${extension}`, ''));

  return `${baseName}_${timestamp}_${randomString}.${extension}`;
};

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} - Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes < 1024) {
    return `${bytes} B`;
  } else if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(2)} KB`;
  } else {
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  }
};

/**
 * Extract a user-friendly error message from various error formats
 * Handles API error objects, error strings, and other error types
 * @param {any} error - The error (can be string, object, or any type)
 * @returns {string} - A user-friendly error message
 */
export const getErrorMessage = (error) => {
  // Handle null/undefined
  if (!error) {
    return 'An unknown error occurred';
  }

  // Handle string errors
  if (typeof error === 'string') {
    return error;
  }

  // Handle error objects with message property
  if (error?.message) {
    return error.message;
  }

  // Handle API error responses with error property
  if (error?.error) {
    // If error.error is also an object, recursively extract
    if (typeof error.error === 'object') {
      return getErrorMessage(error.error);
    }
    return error.error;
  }

  // Handle axios/fetch error responses
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return error.message || error.toString();
  }

  // Handle objects with custom error fields
  if (error?.errorMessage) {
    return error.errorMessage;
  }

  if (error?.statusText) {
    return error.statusText;
  }

  // Handle arrays of errors (take first one)
  if (Array.isArray(error) && error.length > 0) {
    return getErrorMessage(error[0]);
  }

  // Try to stringify if it's an object
  if (typeof error === 'object') {
    try {
      const errorStr = JSON.stringify(error);
      // Don't return huge JSON strings
      if (errorStr.length < 200) {
        return errorStr;
      }
    } catch (e) {
      // If JSON.stringify fails, fall through
    }
  }

  // Default fallback
  return 'An unexpected error occurred';
};
